/**
 * Allauth headless browser client (`/_allauth/browser/v1/`).
 * Based on the official django-allauth React SPA example. Session cookies +
 * X-CSRFToken; auth state changes are broadcast via an `allauth.auth.change`
 * DOM event that AuthProvider listens for. MFA flows are out of NEW YENDA v1
 * (decision #4) — no 2FA endpoints here.
 */
import { getCSRFToken } from "./django";

const ACCEPT_JSON = { accept: "application/json" };

const BASE_URL = "/_allauth/browser/v1";

export const URLs = Object.freeze({
  CONFIG: "/config",
  LOGIN: "/auth/login",
  SESSION: "/auth/session",
  REQUEST_PASSWORD_RESET: "/auth/password/request",
  RESET_PASSWORD: "/auth/password/reset",
  VERIFY_EMAIL: "/auth/email/verify",
});

export interface AllauthResponse {
  status: number;
  data?: {
    user?: Record<string, unknown>;
    email?: string;
    flows?: Array<{ id: string; is_pending?: boolean; types?: string[] }>;
    methods?: Array<Record<string, unknown>>;
  };
  meta?: {
    is_authenticated?: boolean;
  };
  errors?: Array<{ message: string; code: string; param?: string }>;
}

async function request(
  method: string,
  path: string,
  data?: unknown,
  headers?: Record<string, string>,
): Promise<AllauthResponse> {
  const options: RequestInit = {
    method,
    headers: { ...ACCEPT_JSON, ...headers },
    // Browser client: always send the session cookie.
    credentials: "include",
  };

  const reqHeaders = options.headers as Record<string, string>;
  if (path !== URLs.CONFIG) {
    reqHeaders["X-CSRFToken"] = getCSRFToken() ?? "";
  }
  if (typeof data !== "undefined") {
    options.body = JSON.stringify(data);
    reqHeaders["Content-Type"] = "application/json";
  }

  const resp = await fetch(BASE_URL + path, options);
  const msg: AllauthResponse = await resp.json();

  // Broadcast auth state transitions so AuthProvider can react.
  if (
    [401, 410].includes(msg.status) ||
    (msg.status === 200 && msg.meta?.is_authenticated)
  ) {
    document.dispatchEvent(
      new CustomEvent("allauth.auth.change", { detail: msg }),
    );
  }

  return msg;
}

export function login(data: {
  password: string;
  email?: string;
  username?: string;
}): Promise<AllauthResponse> {
  return request("POST", URLs.LOGIN, data);
}

export function logout(): Promise<AllauthResponse> {
  return request("DELETE", URLs.SESSION);
}

export function getAuth(): Promise<AllauthResponse> {
  return request("GET", URLs.SESSION);
}

export function getConfig(): Promise<AllauthResponse> {
  return request("GET", URLs.CONFIG);
}

export function requestPasswordReset(email: string): Promise<AllauthResponse> {
  return request("POST", URLs.REQUEST_PASSWORD_RESET, { email });
}

/** Validate a reset key (from the emailed deep link) before showing the form. */
export function getPasswordResetInfo(key: string): Promise<AllauthResponse> {
  return request("GET", URLs.RESET_PASSWORD, undefined, {
    "X-Password-Reset-Key": key,
  });
}

/**
 * Complete a password reset. Success is a 200 (session started) or a 401
 * (reset done, sign-in required) — only a 400 carries field errors.
 */
export function resetPassword(data: {
  key: string;
  password: string;
}): Promise<AllauthResponse> {
  return request("POST", URLs.RESET_PASSWORD, data);
}

/** Look up which address an emailed verification key belongs to. */
export function getEmailVerificationInfo(
  key: string,
): Promise<AllauthResponse> {
  return request("GET", URLs.VERIFY_EMAIL, undefined, {
    "X-Email-Verification-Key": key,
  });
}

/** Confirm an email address. Like resetPassword, only a 400 is a failure. */
export function verifyEmail(key: string): Promise<AllauthResponse> {
  return request("POST", URLs.VERIFY_EMAIL, { key });
}
